import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DataTableState } from '@beyond-js/ui/react';

/**
 * A `DataTable`'s state in the address (the family's rule: a list keeps its search, order and page in
 * the address, so reload and history reproduce it). The table never reads the address itself; this
 * reads it once per address and writes each change the person makes, replacing the entry, under its own
 * prefix so two tables on one page keep apart (`members-query`, `audit-page`).
 */
export class TableAddress {
	#prefix: string;

	constructor(prefix: string) {
		this.#prefix = prefix;
	}

	read(parameters: URLSearchParams): Partial<DataTableState> {
		const get = (name: string) => parameters.get(`${this.#prefix}-${name}`);
		const page = Number(get('page'));
		return {
			query: get('query') ?? '',
			sort: get('sort'),
			direction: get('direction') === 'descending' ? 'descending' : 'ascending',
			page: Number.isInteger(page) && page > 0 ? page : 1
		};
	}

	write(parameters: URLSearchParams, state: DataTableState): URLSearchParams {
		const next = new URLSearchParams(parameters);
		const set = (name: string, value: string | null, fallback: string | null) => {
			if (value === null || value === fallback || value === '') next.delete(`${this.#prefix}-${name}`);
			else next.set(`${this.#prefix}-${name}`, value);
		};
		set('query', state.query, '');
		set('sort', state.sort, null);
		set('direction', state.sort ? state.direction : null, 'ascending');
		set('page', String(state.page), '1');
		return next;
	}
}

/** The table's state from the address and the writer of its changes, for one prefix */
export function useTableAddress(prefix: string): [Partial<DataTableState>, (state: DataTableState) => void] {
	const [parameters, setParameters] = useSearchParams();
	const address = useMemo(() => new TableAddress(prefix), [prefix]);
	const state = useMemo(() => address.read(parameters), [address, parameters]);
	const change = useCallback((next: DataTableState) => setParameters(current => address.write(current, next), { replace: true }), [address, setParameters]);
	return [state, change];
}

